import React, { useState, useEffect } from 'react';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import vulnApi from '../../services/vulnApi';
import useVulnFilters from '../../hooks/useVulnFilters';
import VulnFilterBar from '../../components/vuln/VulnFilterBar';

function VulnDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const { customerId, assetGroupId, setCustomerId, setAssetGroupId } = useVulnFilters();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await vulnApi.getDashboard(customerId || undefined, assetGroupId || undefined);
        setDashboard(data);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [customerId, assetGroupId]);

  const severityColor = (sev) => ({
    critical: 'text-rose-500',
    high: 'text-orange-500',
    medium: 'text-amber-500',
    low: 'text-sky-500',
    info: 'text-slate-400',
  }[sev] || 'text-slate-400');

  const statusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-100 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400',
      running: 'bg-amber-100 dark:bg-amber-400/20 text-amber-600 dark:text-amber-400',
      failed: 'bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400',
      pending: 'bg-slate-100 dark:bg-slate-400/20 text-slate-600 dark:text-slate-400',
    };
    return styles[status] || styles.pending;
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">

            <div className="sm:flex sm:justify-between sm:items-center mb-8">
              <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold mb-4 sm:mb-0">
                Vulnerability Dashboard
              </h1>
              <VulnFilterBar
                customerId={customerId}
                assetGroupId={assetGroupId}
                onCustomerChange={setCustomerId}
                onAssetGroupChange={setAssetGroupId}
              />
            </div>

            {loading && (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
              </div>
            )}

            {error && (
              <div className="bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400 px-4 py-3 rounded-sm mb-6">
                {error}
              </div>
            )}

            {!loading && !error && !dashboard && (
              <div className="text-center py-16 text-slate-500 dark:text-slate-400">
                No vulnerability data yet. Create a scan to get started.
              </div>
            )}

            {!loading && !error && dashboard && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5">
                    <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase mb-1">Total Assets</div>
                    <div className="text-3xl font-bold text-blue-500">{dashboard.total_assets}</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5">
                    <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase mb-1">Total Findings</div>
                    <div className="text-3xl font-bold text-amber-500">{dashboard.total_findings}</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5">
                    <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase mb-1">Critical / High</div>
                    <div className="text-3xl font-bold text-rose-500">
                      {(dashboard.findings_by_severity?.critical || 0) + (dashboard.findings_by_severity?.high || 0)}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5">
                    <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase mb-1">Open Findings</div>
                    <div className="text-3xl font-bold text-indigo-500">{dashboard.findings_by_status?.open || 0}</div>
                  </div>
                </div>

                {/* Severity Bars + Top CVEs */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100 mb-4">Findings by Severity</h2>
                    <div className="space-y-3">
                      {['critical', 'high', 'medium', 'low', 'info'].map(sev => {
                        const count = dashboard.findings_by_severity?.[sev] || 0;
                        const total = dashboard.total_findings || 1;
                        const pct = Math.round((count / total) * 100);
                        const colors = { critical: 'bg-rose-500', high: 'bg-orange-500', medium: 'bg-amber-500', low: 'bg-sky-500', info: 'bg-slate-400' };
                        return (
                          <div key={sev}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="capitalize text-slate-600 dark:text-slate-300">{sev}</span>
                              <span className="font-medium text-slate-800 dark:text-slate-200">{count}</span>
                            </div>
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                              <div className={`${colors[sev]} h-2 rounded-full`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5">
                    <h2 className="font-semibold text-slate-800 dark:text-slate-100 mb-4">Top CVEs</h2>
                    <div className="overflow-x-auto">
                      <table className="table-auto w-full dark:text-slate-300">
                        <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                          <tr>
                            <th className="py-2 text-left">CVE</th>
                            <th className="py-2 text-left">CVSS</th>
                            <th className="py-2 text-left">Severity</th>
                            <th className="py-2 text-right">Assets</th>
                          </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                          {(dashboard.top_cves || []).map((cve, i) => (
                            <tr key={i}>
                              <td className="py-2 font-medium">{cve.cve_id}</td>
                              <td className="py-2">{cve.cvss}</td>
                              <td className={`py-2 capitalize ${severityColor(cve.severity)}`}>{cve.severity}</td>
                              <td className="py-2 text-right">{cve.affected_assets}</td>
                            </tr>
                          ))}
                          {(!dashboard.top_cves || dashboard.top_cves.length === 0) && (
                            <tr><td colSpan={4} className="py-4 text-center text-slate-400">No CVEs found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Recent Scans */}
                <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-5">
                  <h2 className="font-semibold text-slate-800 dark:text-slate-100 mb-4">Recent Scans</h2>
                  <div className="overflow-x-auto">
                    <table className="table-auto w-full dark:text-slate-300">
                      <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className="py-2 text-left">Name</th>
                          <th className="py-2 text-left">Type</th>
                          <th className="py-2 text-left">Mode</th>
                          <th className="py-2 text-left">Status</th>
                          <th className="py-2 text-left">Progress</th>
                          <th className="py-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                        {(dashboard.recent_scans || []).map((scan, i) => (
                          <tr key={i}>
                            <td className="py-2 font-medium">{scan.name}</td>
                            <td className="py-2 capitalize">{scan.type || scan.scan_type}</td>
                            <td className="py-2 capitalize">{scan.mode || scan.scan_mode}</td>
                            <td className="py-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(scan.status)}`}>
                                {scan.status}
                              </span>
                            </td>
                            <td className="py-2">{scan.progress != null ? `${scan.progress}%` : '-'}</td>
                            <td className="py-2">{scan.created_at ? new Date(scan.created_at).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                        {(!dashboard.recent_scans || dashboard.recent_scans.length === 0) && (
                          <tr><td colSpan={6} className="py-4 text-center text-slate-400">No scans yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

export default VulnDashboard;
